import React, { useCallback, useEffect, useState } from "react";
import { Ico } from "./productsShared";
import ProductsList from "./ProductsList";
import ProductDetail from "./ProductDetail";
import ProductForm from "./ProductForm";
import { ConfirmDeleteSheet, MoreSheet } from "./ProductsSheets";

const ANIM = 360;

function Toast({ data }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!data) return undefined;
    setShow(true);
    const timer = setTimeout(() => setShow(false), 2600);
    return () => clearTimeout(timer);
  }, [data]);

  if (!data) return null;

  return (
    <div className={`toast${show ? " show" : ""}`}>
      <Ico
        name={data.kind === "alert" ? "alert-circle" : "check-circle-2"}
        style={{ color: data.kind === "alert" ? "#ffd27a" : "#7ee0a3" }}
      />
      {data.msg}
    </div>
  );
}

export default function ProductsMobile({
  products: externalProducts = [],
  loading = false,
  contextLabel = "Établissement",
  references = {},
  permissions = {},
  isVariantsEligible = false,
  onMenu,
  onScan,
  onLoadProduct,
  onSave,
  onSaveVariants,
  onRequestDelete,
  onAction,
  openCreateSignal,
  onFormVisibilityChange,
  referenceNotice,
}) {
  const [products, setProducts] = useState(externalProducts);
  const [screens, setScreens] = useState([
    { key: "list", type: "list", cls: "screen screen--in" },
  ]);
  const [toast, setToast] = useState(null);
  const [moreFor, setMoreFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [saving, setSaving] = useState(false);
  const formVisible = screens[screens.length - 1]?.type === "form";
  useEffect(() => { onFormVisibilityChange?.(formVisible); }, [formVisible, onFormVisibilityChange]);

  useEffect(() => {
    setProducts(externalProducts);
    setScreens((current) => {
      const top = current[current.length - 1];
      if (
        top?.type === "detail" &&
        !externalProducts.some((product) => product.id === top.id)
      ) {
        return [{ key: "list", type: "list", cls: "screen screen--in" }];
      }
      return current;
    });
  }, [externalProducts]);

  const showToast = useCallback(
    (msg, kind) => setToast({ msg, kind, id: Date.now() }),
    []
  );

  const pushScreen = useCallback((screen) => {
    setScreens((current) => [
      ...current,
      {
        ...screen,
        key: `${screen.type}:${Date.now()}`,
        cls: "screen screen--push",
      },
    ]);
    setTimeout(() => {
      setScreens((current) =>
        current.map((item, index) =>
          index === current.length - 1
            ? { ...item, cls: "screen screen--in" }
            : item
        )
      );
    }, 24);
  }, []);

  const popScreen = useCallback(() => {
    setScreens((current) =>
      current.length <= 1
        ? current
        : current.map((item, index) =>
            index === current.length - 1
              ? { ...item, cls: "screen screen--out" }
              : item
          )
    );
    setTimeout(
      () =>
        setScreens((current) =>
          current.length <= 1 ? current : current.slice(0, -1)
        ),
      ANIM
    );
  }, []);

  const updateLoadedProduct = useCallback((loadedProduct) => {
    if (!loadedProduct) return;
    setProducts((current) =>
      current.some((product) => product.id === loadedProduct.id)
        ? current.map((product) =>
            product.id === loadedProduct.id ? loadedProduct : product
          )
        : [loadedProduct, ...current]
    );
  }, []);

  const loadProduct = useCallback(
    async (id) => {
      if (!onLoadProduct) {
        return products.find((product) => product.id === id);
      }

      try {
        const loadedProduct = await onLoadProduct(id);
        updateLoadedProduct(loadedProduct);
        return loadedProduct;
      } catch (error) {
        showToast(error.message || "Impossible de charger le produit", "alert");
        return null;
      }
    },
    [onLoadProduct, products, showToast, updateLoadedProduct]
  );

  const openProduct = async (id) => {
    pushScreen({ type: "detail", id });
    await loadProduct(id);
  };

  const openAdd = useCallback(() => {
    if (!permissions.create) return;
    pushScreen({ type: "form", editId: null });
  }, [permissions.create, pushScreen]);

  useEffect(() => {
    if (openCreateSignal) openAdd();
  }, [openAdd, openCreateSignal]);

  const openEdit = async (id) => {
    const product = await loadProduct(id);
    if (!product || !permissions.canEdit?.(product)) return;
    pushScreen({ type: "form", editId: id });
  };

  const handleSave = async (form, editingId) => {
    if (!onSave || saving) return;
    setSaving(true);
    try {
      const savedProduct = await onSave(form, editingId);
      updateLoadedProduct(savedProduct);
      popScreen();
      setTimeout(
        () => showToast(editingId ? "Produit mis à jour" : "Produit créé avec succès"),
        120
      );
    } catch (error) {
      showToast(error.message || "Impossible d'enregistrer le produit", "alert");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVariants = async (payload) => {
    if (!onSaveVariants || saving) return;
    setSaving(true);
    try {
      await onSaveVariants(payload);
      popScreen();
      setTimeout(
        () =>
          showToast(
            payload?.existingProductId
              ? "Produit converti en variantes · historique conservé"
              : "Produit à variantes créé avec succès"
          ),
        120
      );
    } catch (error) {
      showToast(
        error.message ||
          (payload?.existingProductId
            ? "Impossible de convertir le produit en variantes"
            : "Impossible de créer le produit à variantes"),
        "alert"
      );
    } finally {
      setSaving(false);
    }
  };

  const requestSecureAction = (product, action = "delete") => {
    const allowed =
      action === "delete"
        ? permissions.canDelete?.(product)
        : permissions.canManageStatus?.(product);
    if (!product || !allowed) return;
    if (onRequestDelete) {
      onRequestDelete(product);
      return;
    }
    setDeleteFor(product);
  };

  const doLocalDelete = (product) => {
    setProducts((current) => current.filter((item) => item.id !== product.id));
    setDeleteFor(null);
    showToast(`« ${product.name} » supprimé`);
  };

  const moreAction = async (action) => {
    const product = moreFor;
    setMoreFor(null);
    if (!product) return;

    if (action === "edit") {
      setTimeout(() => openEdit(product.id), 60);
      return;
    }
    if (action === "delete" || action === "disable") {
      setTimeout(() => requestSecureAction(product, action), 60);
      return;
    }
    if (onAction) {
      await onAction(action, product);
      return;
    }
    showToast("Action indisponible sur mobile", "alert");
  };

  const lookup = (id) => products.find((product) => product.id === id);
  const canEdit = (product) =>
    Boolean(product && permissions.canEdit?.(product));
  const canDelete = (product) =>
    Boolean(product && permissions.canDelete?.(product));
  const canManageStatus = (product) =>
    Boolean(product && permissions.canManageStatus?.(product));

  return (
    <div className="eap">
      {screens.map((screen) => {
        if (screen.type === "list") {
          return (
            <div className={screen.cls} key={screen.key}>
              <ProductsList
                products={products}
                loading={loading}
                canCreate={permissions.create}
                contextLabel={contextLabel}
                onOpenProduct={openProduct}
                onAdd={openAdd}
                onMenu={onMenu}
                onScan={onScan}
              />
            </div>
          );
        }

        if (screen.type === "detail") {
          const product = lookup(screen.id);
          return (
            <div className={screen.cls} key={screen.key}>
              <ProductDetail
                product={product}
                onBack={popScreen}
                onMore={() => setMoreFor(product)}
                onEdit={() => openEdit(screen.id)}
                onDelete={() => requestSecureAction(product)}
                canEdit={canEdit(product)}
                canDelete={canDelete(product)}
                canManageStatus={canManageStatus(product)}
              />
            </div>
          );
        }

        return (
          <div className={screen.cls} key={screen.key}>
            <ProductForm
              product={screen.editId ? lookup(screen.editId) : null}
              references={references}
              referenceNotice={referenceNotice}
              saving={saving}
              isVariantsEligible={isVariantsEligible}
              onClose={popScreen}
              onSave={handleSave}
              onSaveVariants={handleSaveVariants}
              toast={showToast}
            />
          </div>
        );
      })}

      <MoreSheet
        open={Boolean(moreFor)}
        onClose={() => setMoreFor(null)}
        onAction={moreAction}
        canEdit={canEdit(moreFor)}
        canDelete={canDelete(moreFor)}
        canManageStatus={canManageStatus(moreFor)}
      />
      <ConfirmDeleteSheet
        open={Boolean(deleteFor)}
        product={deleteFor}
        onClose={() => setDeleteFor(null)}
        onConfirm={() => deleteFor && doLocalDelete(deleteFor)}
      />
      <Toast data={toast} />
    </div>
  );
}
